import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Download } from 'lucide-react';
import { ReportHeader } from '../../components/ReportHeader';

const API = process.env.REACT_APP_BACKEND_URL;

// ── helpers ──────────────────────────────────────────────────────────────────

function fmt(n) {
  return (n ?? 0).toLocaleString();
}

function pct(val) {
  return `${Number(val ?? 0).toFixed(2)}%`;
}

function share(val) {
  return `${Number(val ?? 0).toFixed(1)}%`;
}

// Sort partner rows: ESI_MPL_GSI first, then rest alphabetically
function sortPartners(rows) {
  return [...rows].sort((a, b) => {
    const priority = (g) => (g === 'ESI_MPL_GSI' ? 0 : 1);
    const pd = priority(a.partner_group) - priority(b.partner_group);
    if (pd !== 0) return pd;
    return a.partner_group.localeCompare(b.partner_group);
  });
}

// ── sub-components ───────────────────────────────────────────────────────────

function KpiCard({ label, value, testId, accent }) {
  return (
    <div
      className="stat-card"
      data-testid={testId}
      style={{
        borderTop: `3px solid ${accent || '#6366f1'}`,
      }}
    >
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <h2
      style={{
        fontSize: 15,
        fontWeight: 700,
        color: '#1e293b',
        margin: '28px 0 12px',
        letterSpacing: '-0.01em',
      }}
    >
      {children}
    </h2>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function MbrDashboard() {
  const [selectedFy, setSelectedFy]         = useState('');
  const [rangeStart, setRangeStart]         = useState('');
  const [rangeEnd, setRangeEnd]             = useState('');
  const [summary, setSummary]               = useState(null);
  const [partnerRows, setPartnerRows]       = useState([]);
  const [solutionRows, setSolutionRows]     = useState([]);
  const [trendData, setTrendData]           = useState([]);
  const [loading, setLoading]               = useState(false);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [exporting, setExporting]           = useState(false);
  const [error, setError]                   = useState('');

  // Fetch all data whenever selectedFy changes — fast data first, partner-breakdown second
  const fetchAll = useCallback(async (fy, start, end) => {
    if (!fy) return;
    if (fy === 'Custom' && (!start || !end)) return;
    const extra = fy === 'Custom' ? { start_date: start, end_date: end } : {};
    setLoading(true);
    setPartnerLoading(true);
    setError('');
    try {
      const [sumRes, saRes, tRes] = await Promise.all([
        axios.get(`${API}/api/mbr/summary`,        { params: { fy_label: fy, ...extra }, withCredentials: true }),
        axios.get(`${API}/api/mbr/solution-areas`, { params: { fy_label: fy, ...extra }, withCredentials: true }),
        axios.get(`${API}/api/mbr/monthly-trend`,  { params: { fy_label: fy, ...extra }, withCredentials: true }),
      ]);
      setSummary(sumRes.data);
      setSolutionRows(saRes.data);
      setTrendData(tRes.data);
    } catch (e) {
      setError('Failed to load MBR data. Please try again.');
      console.error(e);
    } finally {
      setLoading(false);
    }
    // Partner breakdown loads separately (slower ~10s query)
    try {
      const pRes = await axios.get(`${API}/api/mbr/partner-breakdown`, {
        params: { fy_label: fy, ...extra }, withCredentials: true,
      });
      setPartnerRows(sortPartners(pRes.data));
    } catch (e) {
      console.error('Partner breakdown load failed:', e);
    } finally {
      setPartnerLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll(selectedFy, rangeStart, rangeEnd);
  }, [selectedFy, rangeStart, rangeEnd, fetchAll]);

  // CSV export (partner breakdown only, no PII)
  const handleExport = async () => {
    setExporting(true);
    try {
      const resp = await axios.get(`${API}/api/mbr/export`, {
        params: {
          fy_label: selectedFy,
          ...(selectedFy === 'Custom' ? { start_date: rangeStart, end_date: rangeEnd } : {}),
        },
        withCredentials: true,
        responseType: 'blob',
      });
      const url  = URL.createObjectURL(resp.data);
      const link = document.createElement('a');
      link.href  = url;
      link.download = `mbr_${selectedFy}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  // ── render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <ReportHeader
        reportTitle="MBR Dashboard"
        primaryTable="completion_reports"
        onChange={({ fyLabel, startDate, endDate }) => {
          setSelectedFy(fyLabel);
          setRangeStart(startDate);
          setRangeEnd(endDate);
        }}
      />
      <div data-testid="mbr-dashboard" style={{ padding: '0 16px' }}>
        {/* ── Toolbar: No PII notice + Export ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 10,
            margin: '14px 0 6px',
          }}
        >
          <p
            style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}
            data-testid="mbr-no-pii-notice"
          >
            Aggregate data only — no personally identifiable information
          </p>
          <button
            data-testid="mbr-export-btn"
            className="btn-primary"
            onClick={handleExport}
            disabled={exporting || loading || !selectedFy}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Download size={14} />
            {exporting ? 'Exporting…' : 'Export Partner Breakdown'}
          </button>
        </div>

      {/* ── Error banner ── */}
      {error && (
        <div
          data-testid="mbr-error"
          style={{
            background: '#fef2f2',
            color: '#dc2626',
            border: '1px solid #fecaca',
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && (
        <div
          data-testid="mbr-loading"
          style={{ color: '#64748b', fontSize: 14, padding: '32px 0', textAlign: 'center' }}
        >
          {selectedFy === 'All Time'
            ? 'Crunching all-time data, this can take ~20 seconds…'
            : selectedFy === 'Custom'
              ? 'Crunching data for the selected range, this can take ~20 seconds…'
              : 'Loading…'}
        </div>
      )}

      {!loading && summary && (
        <>
          {/* ── KPI Cards ── */}
          <SectionHeader>Key Metrics — {summary.fy_label}</SectionHeader>
          <div
            className="stats-grid"
            data-testid="mbr-kpi-cards"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}
          >
            <KpiCard
              label="New Registrations"
              value={fmt(summary.registrations)}
              testId="kpi-registrations"
              accent="#f59e0b"
            />
            <KpiCard
              label="Enrollments"
              value={fmt(summary.enrollments)}
              testId="kpi-enrollments"
              accent="#3b82f6"
            />
            <KpiCard
              label="Completions"
              value={fmt(summary.completions)}
              testId="kpi-completions"
              accent="#22c55e"
            />
            <KpiCard
              label="Unique Learners"
              value={fmt(summary.unique_learners)}
              testId="kpi-unique-learners"
              accent="#6366f1"
            />
            <KpiCard
              label="Completion Rate"
              value={pct(summary.completion_rate)}
              testId="kpi-completion-rate"
              accent="#8b5cf6"
            />
            <KpiCard
              label="AI Completions"
              value={fmt(summary.ai_completions)}
              testId="kpi-ai-completions"
              accent="#06b6d4"
            />
          </div>

          {/* ── Monthly Trend Chart ── */}
          <SectionHeader>Monthly Trend</SectionHeader>
          <div
            className="report-card"
            data-testid="mbr-trend-chart"
            style={{ padding: '20px 16px 8px' }}
          >
            {trendData.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
                No trend data for this fiscal year.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trendData} margin={{ top: 4, right: 24, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="month_label"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={false}
                    width={52}
                  />
                  <Tooltip
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 6,
                      border: '1px solid #e2e8f0',
                    }}
                    formatter={(value, name) => [value.toLocaleString(), name]}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                    iconType="circle"
                  />
                  <Line
                    type="monotone"
                    dataKey="enrollments"
                    name="Enrollments"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="completions"
                    name="Completions"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="registrations"
                    name="Registrations"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* ── Partner Breakdown Table ── */}
          <SectionHeader>Partner Breakdown</SectionHeader>
          <div className="report-card" data-testid="mbr-partner-table">
            {partnerLoading ? (
              <div style={{ color: '#64748b', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
                Loading partner data…
              </div>
            ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Partner Group</th>
                    <th style={{ textAlign: 'right' }}>Partners</th>
                    <th style={{ textAlign: 'right' }}>Registrations</th>
                    <th style={{ textAlign: 'right' }}>Enrollments</th>
                    <th style={{ textAlign: 'right' }}>Completions</th>
                    <th style={{ textAlign: 'right' }}>Completion Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {partnerRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0' }}>
                        No data for this fiscal year.
                      </td>
                    </tr>
                  ) : (
                    partnerRows.map((row) => (
                      <tr key={row.partner_group} data-testid={`partner-row-${row.partner_group}`}>
                        <td>
                          <span
                            style={{
                              fontFamily: 'monospace',
                              fontSize: 12,
                              background: '#f1f5f9',
                              padding: '2px 6px',
                              borderRadius: 4,
                            }}
                          >
                            {row.partner_group}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>{fmt(row.partner_count)}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(row.registrations)}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(row.enrollments)}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(row.completions)}</td>
                        <td style={{ textAlign: 'right' }}>{pct(row.completion_rate)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            )}
          </div>

          {/* ── Solution Areas Table ── */}
          <SectionHeader>Solution Areas</SectionHeader>
          <div
            className="report-card"
            data-testid="mbr-solution-areas-table"
            style={{ marginBottom: 32 }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Solution Area</th>
                    <th style={{ textAlign: 'right' }}>Courses</th>
                    <th style={{ textAlign: 'right' }}>Enrollments</th>
                    <th style={{ textAlign: 'right' }}>Completions</th>
                    <th style={{ textAlign: 'right' }}>Completion Rate</th>
                    <th style={{ textAlign: 'right' }}>Enroll Share</th>
                  </tr>
                </thead>
                <tbody>
                  {solutionRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0' }}>
                        No data for this fiscal year.
                      </td>
                    </tr>
                  ) : (
                    solutionRows.map((row) => (
                      <tr key={row.area_code} data-testid={`sa-row-${row.area_code}`}>
                        <td>{row.area_name}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(row.course_count)}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(row.enrollments)}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(row.completions)}</td>
                        <td style={{ textAlign: 'right' }}>{pct(row.completion_rate)}</td>
                        <td style={{ textAlign: 'right' }}>{share(row.enroll_share)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      </div>
    </>
  );
}
